<template>
    <CustomModal :isOpen="props.show" @close="emit('close')">
        <template #content>
            <h5 class="text-slate-800 dark:text-slate-100 text-2xl font-medium mb-2">Share trip</h5>
            <div class="mb-4"></div>
            <div class="flex items-center justify-between mb-2">
                <div class="flex flex-col">
                    <p class="text-slate-800 dark:text-slate-100 text-base font-medium">
                        Create public link
                    </p>
                    <p class="text-slate-500 dark:text-slate-400 text-sm font-normal">
                        Anyone with link can view the page.
                    </p>
                </div>
                <label
                    class="switch relative inline-block w-[42px] h-[25px] ml-auto mt-2"
                    id="hs-share-trip-toggle"
                >
                    <icon
                        v-if="isLoading"
                        icon="loader"
                        size="small"
                        class="relative right-10 mt-0.5"
                    />
                    <input
                        class="opacity-0 w-0 h-0"
                        type="checkbox"
                        :disabled="isLoading"
                        v-model="trip.is_shared"
                        @change="toggleSharing"
                        id="hs-share-trip-toggle-input"
                    />
                    <span
                        class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-700 transition duration-400 rounded-xl"
                    ></span>
                </label>
            </div>

            <div v-if="trip.is_shared" class="mt-3">
                <div class="input-group mb-3">
                    <div class="input-group-append relative">
                        <input
                            type="text"
                            class="form-control w-full h-[50px] rounded-lg bg-gray-100 dark:bg-slate-700 border-none pr-2 cursor-pointer relative"
                            :value="publicLink"
                            readonly
                        />
                        <button
                            class="btn-base btn-white input-icon bg-gray-100 dark:bg-slate-700"
                            @click="copyLink"
                            @mouseover="showTooltip"
                            @mouseout="hideTooltip"
                            id="hs-share-trip-copy-link"
                        >
                            <i class="fa-regular fa-clone"></i>
                        </button>
                    </div>
                    <div
                        v-if="tooltipVisible"
                        class="tooltip bs-tooltip-top bg-teal-900"
                        role="tooltip"
                    >
                        <div class="arrow"></div>
                        <div class="tooltip-inner">
                            <i
                                v-if="showSuccessIcon"
                                class="fa fa-check mr-1"
                            ></i>
                            {{ tooltipText }}
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="trip.is_shared">
                <div class="flex items-center justify-between mb-2">
                    <div class="flex flex-col">
                        <p class="text-slate-800 dark:text-slate-100 text-base font-medium">
                            Hide semi-sensitive data
                        </p>
                        <p class="text-slate-500 dark:text-slate-400 text-sm font-normal">
                            Hide data like your name, flight number and dates.
                        </p>
                    </div>
                    <label
                        class="switch relative inline-block w-[42px] h-[25px] ml-auto mt-2"
                        id="hs-share-trip-sensitive-toggle"
                    >
                        <icon
                            v-if="isLoading"
                            icon="loader"
                            size="small"
                            class="relative right-10 mt-0.5"
                        />
                        <input
                            class="opacity-0 w-0 h-0"
                            type="checkbox"
                            :disabled="isLoading"
                            v-model="trip.hide_sensitive_share"
                            @change="toggleSensitiveDataShare"
                            id="hs-share-trip-sensitive-toggle-input"
                        />
                        <span
                        class="slider round absolute cursor-pointer inset-0 bg-gray-300 dark:bg-slate-700 transition duration-400 rounded-xl"
                        ></span>
                    </label>
                </div>
            </div>
        </template>
    </CustomModal>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useTripsStore } from '@/store/trips';
import CustomModal from '@/components/ui/modal/custom-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import HsTracking from '@/services/hs-tracking';

const tripsStore = useTripsStore();
const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['updateSharingStatus']);
const tooltipVisible = ref(false);
const tooltipText = ref('Copy link');
const showSuccessIcon = ref(false);
const trip = computed(() => tripsStore.activeTrip);
const publicLink = computed(
    () =>
        document.location.origin +
        '/trip/shared/' +
        tripsStore.activeTrip.share_token
);
const isShared = computed(() => tripsStore.activeTrip.is_shared);
const isLoading = computed(() => tripsStore.loading);

const toggleSharing = () => {
    const message = isShared.value
        ? 'Trip has been successfully shared'
        : 'Trip was successfully unshared.';
    const title = isShared.value ? 'Trip shared' : 'Trip unshared';

    if (isShared.value) {
        HsTracking.trackCustomEvent(
            'pe22692776_toggled_to_share_public_trip_link',
            {
                trip: tripsStore.activeTrip,
            }
        );
    }

    tripsStore.updateTrip({ message, title });
};

const toggleSensitiveDataShare = () => {
    const message = !trip.value.hide_sensitive_share
        ? 'Semi-sensitive data was shared.'
        : 'Semi-sensitive data was successfully hidden.';
    const title = !trip.value.hide_sensitive_share
        ? 'Semi-sensitive data shared'
        : 'Semi-sensitive data hidden';
    tripsStore.updateTrip({ message, title });
};

const copyLink = () => {
    navigator.clipboard.writeText(publicLink.value).then(() => {
        tooltipText.value = 'Copied';
        showSuccessIcon.value = true;

        HsTracking.trackCustomEvent('pe22692776_copied_shareable_trip_link', {
            trip: tripsStore.activeTrip,
        });

        setTimeout(() => {
            tooltipText.value = 'Copy Link';
            showSuccessIcon.value = false;
        }, 2000);
    });
};

const showTooltip = () => {
    tooltipVisible.value = true;
};

const hideTooltip = () => {
    tooltipVisible.value = false;
};
</script>

<style scoped>
.slider {
    &:before {
        position: absolute;
        content: '';
        height: 21px;
        width: 21px;
        left: 2px;
        bottom: 2px;
        background-color: white;
        transition: 0.4s;
        border-radius: 50%;
    }
}

input:checked + .slider {
    background-color: #213e44;

    &:before {
        transform: translateX(17px);
    }
}

.input-icon {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    padding: 0 15px;
    border-radius: 8px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.tooltip {
    position: absolute;
    top: 115px;
    left: 90%;
    transform: translateX(-50%);
    z-index: 1000;
    display: block;
    opacity: 1;
    background-color: #213e44;
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
}

.tooltip-inner {
    display: flex;
    align-items: center;

    & .fa-check {
        margin-right: 5px;
        color: white; /* Success color */
    }
}
</style>
